// SPDX-License-Identifier: AGPL-3.0-or-later

import * as CompanionCommands from '@app/features/companion/commands/CompanionCommands';
import styles from '@app/features/companion/components/CompanionPanel.module.css';
import {Button} from '@app/features/ui/button/Button';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {useEffect, useRef, useState} from 'react';

export function CompanionVoicePreview({
	sceneId,
	disabled,
	autoStart = false,
}: {
	sceneId: string;
	disabled: boolean;
	autoStart?: boolean;
}) {
	const {i18n} = useLingui();
	const resource = useRef<{audio: HTMLAudioElement; url: string} | null>(null);
	const revision = useRef(0);
	const [loading, setLoading] = useState(false);
	const [playing, setPlaying] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const clear = () => {
		revision.current += 1;
		if (resource.current) {
			resource.current.audio.pause();
			URL.revokeObjectURL(resource.current.url);
			resource.current = null;
		}
	};
	useEffect(() => {
		clear();
		setLoading(false);
		setPlaying(false);
		setError(null);
		return clear;
	}, [sceneId, disabled]);

	const preview = async () => {
		if (playing) {
			clear();
			setPlaying(false);
			return;
		}
		const current = ++revision.current;
		setLoading(true);
		setError(null);
		try {
			const blob = await CompanionCommands.previewVoice(sceneId);
			if (current !== revision.current) return;
			const url = URL.createObjectURL(blob);
			const audio = new Audio(url);
			resource.current = {audio, url};
			audio.onended = () => {
				clear();
				setPlaying(false);
			};
			await audio.play();
			if (current === revision.current) setPlaying(true);
		} catch (cause) {
			if (current === revision.current) {
				setError(CompanionCommands.getCompanionCommandErrorMessage(cause));
				setLoading(false);
				clear();
			}
		} finally {
			if (current === revision.current) setLoading(false);
		}
	};
	useEffect(() => {
		if (autoStart && !disabled && sceneId) void preview();
		// 仅挂载时自动开始一次; 后续由按钮控制。
	}, [autoStart, sceneId]);

	return (
		<>
			<Button
				variant="secondary"
				small
				fitContent
				disabled={disabled || loading || !sceneId}
				submitting={loading}
				onClick={() => void preview()}
				data-flx="companion.voice-preview.button"
			>
				{i18n._(playing ? msg({message: 'Stop playback'}) : msg({message: 'Preview voice'}))}
			</Button>
			{error ? (
				<p role="alert" className={styles.errorMessage} data-flx="companion.voice-preview.error">
					{error}
				</p>
			) : null}
		</>
	);
}
